import { EventType } from "@/analytics/analytics-events";
import { OpenTagManagementProperties } from "@/analytics/tag-events-properties";
import {
  FaroButton,
  FaroMenu,
  FaroMenuProps,
  FilterMenuContent,
  FilterMenuOptionType,
  PlusIcon,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { Stack } from "@mui/system";
import { PropsWithChildren, useCallback, useState, type JSX } from "react";
import { TagsManagementDialog } from "./tags-management-dialog";

type TagsMenuProps = Pick<FaroMenuProps, "anchorOrigin" | "transformOrigin"> & {
  /** The list of available tags to select from. */
  options: FilterMenuOptionType[];

  /** The list of selected tags. */
  value: FilterMenuOptionType[];

  /** Whether the menu is open. */
  isOpen: boolean;

  /** The title to show in the menu */
  title: string;

  /** The element the menu should be anchored to. */
  anchorEl: HTMLElement | null;

  /** Whether dark styles should be used. */
  dark?: boolean;

  /** Whether the menu should be disabled for interactions */
  disabled?: boolean;

  /** Whether managing tags should be allowed. @default true */
  allowManageTags?: boolean;

  /** Callback when the user requests a change of selected tags. */
  onChange(tags: FilterMenuOptionType[]): void;

  /** Callback when the user wants to close the menu. */
  onClose?(): void;
};

/**
 * @returns A popup menu that allows the user to select a set of tags out of a filterable list.
 */
export function TagsMenu({
  anchorEl,
  options,
  title,
  isOpen,
  dark,
  disabled,
  allowManageTags = true,
  children,
  value,
  transformOrigin,
  anchorOrigin,
  onChange,
  onClose,
}: PropsWithChildren<TagsMenuProps>): JSX.Element {
  const [isTagsManagementOpen, setIsTagsManagementOpen] = useState(false);

  const onOpenTagsManagement = useCallback(() => {
    Analytics.track<OpenTagManagementProperties>(EventType.openTagManagement, {
      via: "tags menu",
    });

    setIsTagsManagementOpen(true);
  }, []);

  return (
    <>
      <FaroMenu
        anchorEl={anchorEl}
        anchorOrigin={anchorOrigin ?? { vertical: "top", horizontal: "left" }}
        transformOrigin={
          transformOrigin ?? { vertical: "bottom", horizontal: "left" }
        }
        open={isOpen}
        onClose={onClose}
        dark={dark}
        sx={{ transform: "translate(0px, -8px)" }}
      >
        <FilterMenuContent
          headingLabel={title}
          options={options}
          selectedOptions={value}
          onFilterChange={onChange}
          onResetFilter={() => onChange([])}
          disabled={disabled}
          dark={dark}
          keepHeightOnSearch
        >
          {allowManageTags && (
            <FaroButton
              variant="ghost"
              disabled={disabled}
              dark={dark}
              sx={{
                justifyContent: "flex-start",
              }}
              icon={<PlusIcon />}
              onClick={onOpenTagsManagement}
            >
              Manage tags
            </FaroButton>
          )}
        </FilterMenuContent>
        <Stack
          direction="row"
          sx={{ width: "100%", justifyContent: "flex-end" }}
        >
          {children}
        </Stack>
      </FaroMenu>
      {allowManageTags && (
        <TagsManagementDialog
          open={isTagsManagementOpen}
          dark={dark}
          onClose={() => setIsTagsManagementOpen(false)}
        />
      )}
    </>
  );
}
